import React, { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Stack,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { reelMakerApi } from 'components/ReelMaker/ReelMaker.api';
import { ReelPlaybackStage } from 'components/ReelMaker/ReelPlaybackStage';
import { Logger } from 'utils/Logger';
import { PlaylistItemInput } from './Playlists.types';

const LOG_PREFIX = 'BeamOnScreen/playlists/PlaylistPreviewDialog';

export interface PreviewEntry extends PlaylistItemInput {
    reelName: string;
    versionLabel: string;
}

interface LoadedReel {
    entry: PreviewEntry;
    width: number;
    height: number;
    canvas: unknown;
}

export interface PlaylistPreviewDialogProps {
    open: boolean;
    title: string;
    entries: PreviewEntry[];
    onClose: () => void;
}

/** Plays the playlist items back to back (looping), the way a screen will. */
export const PlaylistPreviewDialog: React.FC<PlaylistPreviewDialogProps> = ({ open, title, entries, onClose }) => {
    const [reels, setReels] = useState<LoadedReel[]>([]);
    const [index, setIndex] = useState(0);
    const [round, setRound] = useState(0);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        if (!open) return undefined;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setIndex(0);
        Promise.all(
            entries.map(async (entry) => {
                const snapshot = await reelMakerApi.getReelVersion(entry.reelId, entry.version);
                return { entry, width: snapshot.width, height: snapshot.height, canvas: snapshot.canvas ?? null };
            }),
        )
            .then((loaded) => {
                if (!cancelled) setReels(loaded);
            })
            .catch((err) => {
                Logger.error(LOG_PREFIX, `load failed: ${err}`);
                if (!cancelled) setError(err instanceof Error ? err.message : 'Could not load the reels');
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [open, entries]);

    const current = reels[index];
    const next = () => {
        if (reels.length === 0) return;
        if (index + 1 >= reels.length) setRound((r) => r + 1);
        setIndex((i) => (i + 1) % reels.length);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
                Preview · {title}
                <IconButton aria-label="Close" onClick={onClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {error && <Alert severity="error">{error}</Alert>}
                {loading && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress color="secondary" />
                    </Box>
                )}
                {!loading && !error && reels.length === 0 && <Alert severity="info">Add reels to the playlist to preview it.</Alert>}
                {!loading && current && (
                    <>
                        <Box sx={{ height: '60vh', borderRadius: 1.5, overflow: 'hidden' }}>
                            <ReelPlaybackStage
                                key={`${round}:${index}:${current.entry.reelId}:${current.entry.version}`}
                                width={current.width}
                                height={current.height}
                                canvas={current.canvas}
                                onEnded={next}
                            />
                        </Box>
                        <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: 'wrap', rowGap: 1 }} alignItems="center">
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Playing {index + 1} of {reels.length}
                            </Typography>
                            {reels.map((r, i) => (
                                <Chip
                                    key={`${r.entry.reelId}:${i}`}
                                    size="small"
                                    label={`${i + 1}. ${r.entry.reelName} ${r.entry.versionLabel}`}
                                    color={i === index ? 'secondary' : 'default'}
                                    variant={i === index ? 'filled' : 'outlined'}
                                    onClick={() => setIndex(i)}
                                />
                            ))}
                        </Stack>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PlaylistPreviewDialog;
